<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useMainStore } from "@/stores/main";
import { storeToRefs } from "pinia";

const mainStore = useMainStore();

import { useDisplay } from "@/composables/useDisplay";

const { isMobile } = useDisplay();

const isSmallScreen = ref(false);
const isReactNative = ref(false);

const showFooter = computed(() => {
  if (import.meta.server) return false;
  return !isMobile.value && !isSmallScreen.value && !isReactNative.value;
});

onMounted(() => {
  updateScreenSize();
  window.addEventListener("resize", updateScreenSize);

  const { $reactNative } = useNuxtApp();
  isReactNative.value = $reactNative?.isApp || false;
});

onUnmounted(() => {
  window.removeEventListener("resize", updateScreenSize);
});

function updateScreenSize() {
  isSmallScreen.value = window.matchMedia("(max-width: 768px)").matches;
}

const { globalSettings } = storeToRefs(mainStore);
const footerNavigation: any = computed(() => globalSettings.value?.footer);
const qrCodeImageUrl = computed(() => globalSettings.value?.qrCodeImage?.url || null);
</script>

<template>
  <ClientOnly>
    <footer
      v-if="showFooter"
      class="footer-container"
    >
      <div class="footer-content">
        <!-- Column 1 - Logo -->
        <div class="footer-column footer-logo-column">
          <img
            src="/onepass-mobile-primary-logo.svg"
            alt="OnePass Mobile"
            class="footer-logo"
          />
        </div>

        <!-- Column 2 - About -->
        <div v-if="footerNavigation?.[0]" class="footer-column">
          <h3 class="footer-title">{{ footerNavigation[0].title || 'Good To Know' }}</h3>
          <nav class="footer-links">
            <template v-for="(link, linkIndex) in footerNavigation[0].links" :key="linkIndex">
              <BaseLink :link="link">{{ link.linkText }}</BaseLink>
            </template>
          </nav>
        </div>

        <!-- Column 3 - Help -->
        <div v-if="footerNavigation?.[1]" class="footer-column">
          <h3 class="footer-title">{{ footerNavigation[1].title || 'Member hub' }}</h3>
          <nav class="footer-links">
            <template v-for="(link, linkIndex) in footerNavigation[1].links" :key="linkIndex">
              <BaseLink :link="link">{{ link.linkText }}</BaseLink>
            </template>
          </nav>
        </div>

        <!-- Column 4 - Get the app -->
        <div class="footer-column">
          <h3 class="footer-title">Get the app</h3>
          <div class="qr-code-box">
            <img
              v-if="qrCodeImageUrl"
              :src="useStrapiMedia(qrCodeImageUrl)"
              alt="Download QR Code"
              class="qr-code-img"
            />
            <span v-else class="qr-code-text"></span>
          </div>
        </div>
      </div>
    </footer>
  </ClientOnly>
</template>

<style scoped lang="scss">
.footer-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 24px;
  width: 100%;
  background: #EEEEE9;
}

.footer-content {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
  max-width: 1280px;
}

.footer-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
}

.footer-logo-column {
  max-width: 302px;
}

.footer-logo {
  width: 211px;
  height: auto;
}

.footer-title {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #3A016A;
  margin: 0;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  a {
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #3A016A;
    text-decoration: none;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 0.7;
    }
  }
}

.qr-code-box {
  width: 117px;
  height: 117px;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.qr-code-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-code-text {
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #000000;
  padding: 8px;
}

@media (max-width: 1024px) {
  .footer-content {
    flex-wrap: wrap;
  }
  
  .footer-column {
    min-width: 200px;
  }
}

@media (max-width: 768px) {
  .footer-container {
    display: none;
  }
}
</style>
